import moment from 'moment-timezone';
import { getTimeZone, getFormatDate } from 'utils/timeZone';
import { formatDateToBasic } from 'utils/date_utils_new.js';

export const padNumber = (number, padding) => {
	let zeroes = "";
	for(let i = 0; i < padding; i++) {
		zeroes += "0";
	}

	return (zeroes + number).slice(-padding);
};

export const formatNumberWithDigits = (value, digits) => {
	const { currentLocale } = Realize.i18n;
	return Number(value).toLocaleString(currentLocale.toLowerCase(), { minimumFractionDigits: digits });
};

export const insertSubstringAt = (string, substring, position) => {
	return [string.slice(0, position), substring, string.slice(position)].join('');
};

export function formatProcessInstanceId(processId) {
	if (processId !== undefined && processId !== null) {
		let processIdToString = String(processId);
		const processIdLength = processIdToString.length;
		const maxCount = 6;

		if (processIdLength >= 7)
			return processIdToString.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1.");

		for (let index = 0; index < maxCount - processIdLength; index++) {
			processIdToString = "0" + processIdToString;
		}

		return processIdToString.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1.");
	}

	return "-";
}

export const formatDate = (dateString) => {
	const dateValue = moment.tz(dateString, getFormatDate(dateString), getTimeZone()).utc();
	return formatMomentDate(dateValue, dateString);
};

export const formatDatetime = (datetimeString) => {
	const datetimeValue = moment.tz(datetimeString, getFormatDate(datetimeString), getTimeZone()).utc();
	return formatMomentDate(datetimeValue, datetimeString, 'datetime');
};

export const formatUnixDate = (dateString) => {
	if(isEmptyDate(dateString))
		return null;

	const dateValue = moment.tz(dateString, getFormatDate(dateString), getTimeZone()).utc();
	return formatMomentDate(dateValue, dateString);
};

export const formatUnixDatetime = (datetimeString) => {
	const datetimeValue = moment.tz(datetimeString, getFormatDate(datetimeString), getTimeZone()).utc();
	return formatMomentDate(datetimeValue, datetimeString, 'datetime');
};

const isEmptyDate = (dateString) => {
	return !dateString || dateString == 0;
};

const formatMomentDate = (momentDate, dateString, format = 'date') => {
	if (momentDate.isValid()) {
		return formatDateToBasic(dateString, Realize.i18n.t(`table.cell.formats.${format}`))
	}

	return dateString;
};

export const formatLocalizedDatetimeToISO8601 = (localizedDate) => {
	const momentLocalizedDate = moment.tz(localizedDate, getFormatDate(localizedDate), getTimeZone()).utc();

	if(momentLocalizedDate.isValid()) {
		return formatDateToBasic(localizedDate);
	}

	return localizedDate;
};

export const formatToNumber = (value) => {
	if (value === undefined || value === '' || value === null || isNaN(parseFloat(value)))
		return null;

	if (typeof value === 'number' || !isNaN(value))
		return Number(value);

	const currentLocale = Realize.i18n.currentLocale.toLowerCase();
	const valueString = String(value);
	let valueFormated = 0;

	(currentLocale === 'pt-br' || currentLocale === 'es')
		? valueFormated = parseFloat(valueString.replace(/\./g, '').replace(/,/g, '.'))
		: valueFormated = parseFloat(valueString.replace(/,/g, ''));

	return valueFormated;
};

export const formatCurrency = (value, typeReturn = "string") => {
	if (value === undefined || value === '' || value === null)
		return null;

	const valueString = Number(value).toFixed(2);

	if (typeReturn === 'number')
		return Number(valueString);

	const currentLocale = Realize.i18n.currentLocale.toLowerCase();
	let valueFormated = null;

	(currentLocale === 'pt-br' || currentLocale === 'es')
		? valueFormated = valueString.replace(/\./g, ',').replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1.")
		: valueFormated = valueString.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1,");

	return valueFormated;
}

export const formatDecimal = (value, maxLength = 0, digits = 0, typeReturn = "string") => {
	if (value === undefined || value === '' || value === null)
		return null;

	const currentLocale = Realize.i18n.currentLocale.toLowerCase();
	let valueString = String(value);
	let maskedValue = '';

	const arrNumber = valueString.split(".");
	const decimalCount = {
		'integer': arrNumber[0].length,
		'decimal': arrNumber[1] ? arrNumber[1].length : 0
	};

	const radixPoint = Realize.i18n.t("masks.decimal.radixPoint");
	const integerDigits = arrNumber[0].substr(0, maxLength - digits);
	let decimalDigits = '';

	if (decimalCount.decimal === 0)
		decimalDigits = digits > 1 ? '00' : '0';
	else if (decimalCount.decimal === 1 && decimalCount.decimal !== digits)
		decimalDigits = `${arrNumber[1]}0`;
	else
		decimalDigits = arrNumber[1].substr(0, digits);

	valueString = `${integerDigits}${radixPoint}${decimalDigits}`;

	if (typeReturn === 'number') {
		valueString = valueString.replace(/,/g, '.');
		return Number(valueString);
	}

	(currentLocale === 'pt-br' || currentLocale === 'es')
		? maskedValue = valueString.replace(/\./g, ',')
		: maskedValue = valueString;

	return maskedValue;
};

export const changeDecimalString = (value, prevMaxLength = 0, prevDigits = 0, type, prefix, isAPIJS) => {
	const radix = Realize.i18n.t("masks.decimal.radixPoint");
	const separator = radix === "," ? "." : ",";
	const maxLength = type === "currency" ? 15 : prevMaxLength;
	const digits = type === "currency" ? 2 : prevDigits;
	let maxLengthInteger = (maxLength - digits) <= 0 ? 1 : (maxLength - digits);
	let maxLengthDecimals = digits <= 0 ? 1 : digits;
	let maxLengthFormatted = maxLength;
	let valueFormatted = String(value);
	let negative = "";

	if (prefix) {
		valueFormatted = valueFormatted.replace(prefix, "").trim();
	}

	if (isAPIJS)
		valueFormatted = valueFormatted.replace(/[^-+.\d]/g, '');
	else
		valueFormatted = valueFormatted.replace(/[^-+\d]/g, '');

	if (valueFormatted === "" || valueFormatted === undefined || valueFormatted === null || isNaN(valueFormatted.replace(/[-+.,]/g, "")))
		return "";

	if (!!valueFormatted.match(/[+]/g))
		valueFormatted = valueFormatted.replace(/[-+]/g, "");

	if (!!valueFormatted.match(/[-]/g)) {
		negative = "-";
		maxLengthFormatted = maxLengthFormatted + 1;
		valueFormatted = `-${valueFormatted.replace(/[-+]/g, "")}`;
	}

	if (type === "number") {
		valueFormatted = valueFormatted.substr(0, maxLengthFormatted).replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${separator}`);
	}
	else if (isAPIJS) {
		if (valueFormatted.match(/[.]/g)) {
			const integers = (valueFormatted.split(".")[0] || "").replace(/[-+]/g, "").substr(0, maxLengthInteger);
			const decimals = (valueFormatted.split(".")[1] || "").replace(/[-+]/g, "").substr(0, maxLengthDecimals);

			if(decimals.length < maxLengthDecimals){
				valueFormatted = `${negative}${integers.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${separator}`)}${radix}${decimals}${new Array((maxLengthDecimals - decimals.length) + 1).join('0')}`;
			}
			else{
				valueFormatted = `${negative}${integers.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${separator}`)}${radix}${decimals}`;
			}
		}
		else {
			valueFormatted = valueFormatted.replace(/[-+]/g, "").substr(0, maxLengthInteger);
			valueFormatted = Number(valueFormatted).toFixed(digits).split(".").join(radix);

			const integers = valueFormatted.split(radix)[0];
			const decimals = valueFormatted.split(radix)[1];

			valueFormatted = `${negative}${integers.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${separator}`)}${radix}${decimals}`;
		}
	}
	else if (valueFormatted.replace(/[-+.,]/g, "").length > digits) {
		valueFormatted = valueFormatted.substr(0, maxLengthFormatted);

		const integers = valueFormatted.substr(0, valueFormatted.length - digits);
		const decimals = valueFormatted.substr(valueFormatted.length - digits, valueFormatted.length);

		valueFormatted = `${integers.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${separator}`)}${radix}${decimals}`;
	}

	return `${prefix || ""}${valueFormatted}`;
}

export const getApiValueDecimalNumber = (value, prefix) => {
	const radix = Realize.i18n.t("masks.decimal.radixPoint");
	const separator = radix === "," ? "." : ",";
	let valueWithoutPrefix = String(value || "").split(prefix || "").join("");
	let valueFormatted = valueWithoutPrefix;

	if (valueFormatted === "-" || valueFormatted === "" || valueFormatted === undefined || valueFormatted === null || isNaN(valueFormatted.replace(/[-+.,]/g, "")))
		return null;

	valueFormatted = valueWithoutPrefix ? String(valueWithoutPrefix).split(separator).join("") : valueWithoutPrefix;
	valueFormatted = valueFormatted ? String(valueFormatted).split(radix).join(".") : valueFormatted;

	return Number(valueFormatted);
}

export const FormattingUtils = {
	padNumber,
	formatNumberWithDigits,
	insertSubstringAt,
	formatProcessInstanceId,
	formatDate,
	formatDatetime,
	formatUnixDate,
	formatUnixDatetime,
	formatLocalizedDatetimeToISO8601,
	formatToNumber,
	formatCurrency,
	formatDecimal,

	changeDecimalString,
	getApiValueDecimalNumber
};

export default FormattingUtils;
